// client/src/dnd/emitter_tool/components/panels/ImportPanel.js
import React, { useEffect, useRef, useState } from "react";

const OPEN_KEY = "EmitterTool:ImportPanelOpen";

function CollapsibleBox({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        overflow: "hidden",
        background: "#0b0f17",
        width: "100%",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          cursor: "pointer",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
        }}
      >
        <span>{title}</span>
        <span style={{ opacity: 0.75 }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: 8 }}>{children}</div>}
    </div>
  );
}

const btnStyle = {
  background: "#0f172a",
  color: "#e5e7eb",
  border: "1px solid #334155",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 12,
  lineHeight: 1,
  cursor: "pointer",
};

// Robust parser that accepts:
// - pure JSON
// - `export default { ... };` (with or without leading comments)
// - `module.exports = { ... };`
function parsePossiblyJS(text) {
  const raw = String(text || "").trim();
  if (!raw) throw new Error("Empty file.");

  // Fast path: plain JSON
  if (raw[0] === "{" || raw[0] === "[") {
    return JSON.parse(raw);
  }

  // Try JSON anyway (in case it's JSON with some whitespace)
  try {
    const j = JSON.parse(raw);
    if (j && typeof j === "object") return j;
  } catch {
    // ignore, fall through to JS-module-style parsing
  }

  // Try to find `export default { ... }`
  let m =
    raw.match(/export\s+default\s+(\{[\s\S]*\})\s*;?\s*$/m) ||
    raw.match(/module\.exports\s*=\s*(\{[\s\S]*\})\s*;?\s*$/m);

  if (!m) {
    // As a last-ditch attempt, grab the first `{` and last `}` and try to parse that
    const firstBrace = raw.indexOf("{");
    const lastBrace = raw.lastIndexOf("}");
    if (firstBrace >= 0 && lastBrace > firstBrace) {
      const candidate = raw.slice(firstBrace, lastBrace + 1);
      try {
        const cleaned = candidate
          .replace(/,\s*}/g, "}")
          .replace(/,\s*]/g, "]");
        return JSON.parse(cleaned);
      } catch {
        // fall through
      }
    }
    throw new Error(
      "Could not parse file. Expect JSON or `export default { ... }` / `module.exports = { ... }`."
    );
  }

  // Clean trailing commas inside the captured object literal before JSON.parse
  let objStr = m[1]
    .replace(/,\s*}/g, "}")
    .replace(/,\s*]/g, "]");

  return JSON.parse(objStr);
}

export default function ImportPanel({
  effectName,
  setEffectName,
  getSnapshot, // () => full JSON-safe object { meta/global/parent/subEmitters }
  applySnapshot, // (obj) => void : sets parent + subEmitters etc.
  onExport, // optional legacy hook
  onImport, // optional legacy hook (text) => void
  defaultOpen = true,
  title = "Import / Export",
}) {
  const fileInputRef = useRef(null);

  const handleImportClick = () => fileInputRef.current?.click();

  function downloadJSON(filename, dataObj) {
    const blob = new Blob([JSON.stringify(dataObj, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.download = filename;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const handleExport = () => {
    try {
      if (typeof getSnapshot === "function") {
        const snap = getSnapshot();
        const fname = `${(snap?.name || effectName || "effect").replace(
          /[^\w.-]+/g,
          "_"
        )}.json`;
        downloadJSON(fname, snap);
        return;
      }
      if (typeof onExport === "function") {
        onExport();
        return;
      }
      alert("Export not wired: pass getSnapshot() or onExport().");
    } catch (err) {
      console.error("[ImportPanel] Export failed:", err);
      alert(`Export failed: ${err.message || err}`);
    }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      if (typeof applySnapshot === "function") {
        const obj = parsePossiblyJS(text);
        applySnapshot(obj);
      } else if (typeof onImport === "function") {
        onImport(text);
      } else {
        alert("Import not wired: pass applySnapshot(obj) or onImport(text).");
      }
    } catch (err) {
      console.error("[ImportPanel] Failed to import preset:", err);
      alert(`Import failed: ${err.message || err}`);
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <CollapsibleBox title={title} defaultOpen={defaultOpen}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
          }}
        >
          <input
            type="text"
            placeholder="Effect name"
            value={effectName || ""}
            onChange={(e) => setEffectName?.(e.target.value)}
            style={{
              flex: "0 0 25%",
              minWidth: 90,
              background: "#0b0d12",
              color: "white",
              border: "1px solid #374151",
              borderRadius: 8,
              padding: "6px 10px",
            }}
          />

          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <button style={btnStyle} onClick={handleExport}>
              Export .json
            </button>
            <button style={btnStyle} onClick={handleImportClick}>
              Import .json / .js
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".js,.json,application/json,text/javascript"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>
      </CollapsibleBox>
    </div>
  );
}

export { ImportPanel };
